<script setup lang="ts">
    import { inject, reactive, onMounted, watch } from 'vue';
    import { TaskErrorTask, TaskError, TaskErrorComment } from './HealthManager.types';
    import Modal from '@shared/components/Modal.vue';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { AccountAccess } from '@cwd/Account/Account.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { BackOfficeUser } from '@views/BackOffice/User/BackOfficeUser';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import TaskErrorComments from './TaskErrorComments.vue';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import { ContextAccess } from '@shared/Root/Context.access';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const accountAccess: AccountAccess = injectorAccess.injector.get(AccountAccess);

    const emit = defineEmits<{
        (e: 'close'): void;
        (e: 'assignToUser', taskError: TaskError, userId: string): void;
        (e: 'switchAccount', accountId: string): void;
        (e: 'saveComments', taskErrorId: string, comments: TaskErrorComment[]): void;
    }>();

    const props = defineProps<{
        show: boolean;
        taskError: TaskError | null;
        accountId: string | null;
        backOfficeUsers: BackOfficeUser[] | null;
        selectedTabIndex?: number;
    }>();

    const state = reactive<{
        selectedBackOfficeUserId: string[];
    }>({
        selectedBackOfficeUserId: [],
    });

    onMounted(() => {
        watch(
            () => props.taskError,
            (newVal) => {
                if (!newVal) return;
                const ownedBy = newVal?.OwnedBy;
                const userId = ownedBy && ownedBy.UserId ? ownedBy.UserId : null;
                state.selectedBackOfficeUserId = userId ? [userId] : [];
            },
            { immediate: true }
        );
    });

    function goToOject(task: TaskErrorTask): string {
        const currentAccountId = accountAccess!.state.currentAccount?.AccountId;

        let type;

        switch (task.TypeName) {
            case 'SendIssue':
                type = 'Issues';
                break;
            case 'SendSms':
                type = 'TextMessages';
                break;
            case 'Import':
                type = 'Lists';
                break;
        }

        // If item has no account info or belongs to current account, use direct URL
        if (props.accountId === currentAccountId) {
            return `/${type}/${task.ObjectId}`;
        }

        // Otherwise, redirect through account switcher
        return `/Account/${props.accountId}?returnurl=${window.location.origin + '/' + type + '/' + task.ObjectId}`;
    }

    const assignToUser = () => {
        if (!props.taskError) return;
        const userId = state.selectedBackOfficeUserId[0];
        emit('assignToUser', props.taskError, userId);
    };

    const assignToMe = () => {
        const currentUser = contextAccess.state.currentUser;
        const userId = currentUser?.UserId || '';
        if (!userId) return;
        state.selectedBackOfficeUserId = [userId];
        assignToUser();
    };

    const switchAccount = (accountId: string) => {
        emit('switchAccount', accountId);
    };
</script>

<template>
    <Modal :title="'Task Error'" :show="show" @close="$emit('close')" :size="'lg'">
        <template #content v-if="taskError">
            <Tabs :queryParam="false" :selectedIndex="props.selectedTabIndex || 0">
                <tab title="Overview">
                    <div class="form-group d-flex gap-sm align-items-end flex-wrap">
                        <div class="flex-1" style="min-width: 300px">
                            <MultiSelect
                                label="Assign to"
                                :items="backOfficeUsers"
                                v-model:selectedItems="state.selectedBackOfficeUserId"
                                :selectProp="'UserId'"
                                :displayProp="'UserName'"
                                :selectOnly="'UserId'"
                                :maxSelectable="1" />
                        </div>
                        <button :disabled="state.selectedBackOfficeUserId.length === 0" class="btn btn-default" @click="assignToUser">Assign</button>
                        <button class="btn btn-default" @click="assignToMe">Assign to me</button>
                    </div>

                    <div style="column-count: 2">
                        <p>
                            <strong>Task ID:</strong>
                            <br />
                            {{ taskError.TaskId }}
                        </p>
                        <p>
                            <strong>Account:</strong>
                            <br />
                            <a href="javascript:void(0)" role="button" @click="switchAccount(taskError.Account.AccountId)">{{ taskError.Account.Name }}</a>
                        </p>
                        <p>
                            <strong>Object:</strong>
                            <br />
                            <template
                                v-if="taskError.Task.TypeName === 'SendIssue' || taskError.Task.TypeName === 'SendSms' || taskError.Task.TypeName === 'Import'">
                                <a :href="goToOject(taskError.Task)">{{ taskError.Task.Object.ObjectName ?? taskError.Task.ObjectId }}</a>
                            </template>
                            <template v-else>
                                {{ taskError.Task.ObjectId }}
                            </template>
                        </p>
                        <p>
                            <strong>Type Name:</strong>
                            <br />
                            {{ taskError.Task.TypeName }}
                        </p>
                        <p>
                            <strong>Status:</strong>
                            <br />
                            {{ taskError.Task.StatusName }}
                        </p>
                        <template v-if="taskError.Task.TypeName === 'SendIssue' && taskError.Task.IssueStatus !== undefined">
                            <p>
                                <strong>Issue Status:</strong>
                                <br />
                                {{ taskError.Task.IssueStatusName }} ({{ taskError.Task.IssueStatus }})
                            </p>
                            <p>
                                <strong>Resend to Existing:</strong>
                                <br />
                                {{ taskError.Task.ResendToExistingRecipients ? 'Yes' : 'No' }}
                            </p>
                        </template>
                        <p>
                            <strong>Assigned To:</strong>
                            <br />
                            {{ taskError.Task.Assigned || '-' }}
                        </p>
                        <p>
                            <strong>Created:</strong>
                            <br />
                            {{ formatDateTime(taskError.Task.Created) }}
                        </p>
                        <p>
                            <strong>Created By:</strong>
                            <br />
                            {{ taskError.Task.CreatedBy?.Name || '-' }}
                        </p>
                        <p>
                            <strong>Scheduled:</strong>
                            <br />
                            {{ taskError.Task.Scheduled ? formatDateTime(taskError.Task.Scheduled) : '-' }}
                        </p>
                        <p>
                            <strong>Started:</strong>
                            <br />
                            {{ taskError.Task.Started ? formatDateTime(taskError.Task.Started) : '-' }}
                        </p>
                        <p>
                            <strong>Ended:</strong>
                            <br />
                            {{ taskError.Task.Ended ? formatDateTime(taskError.Task.Ended) : '-' }}
                        </p>
                        <p>
                            <strong>Recurring:</strong>
                            <br />
                            {{ taskError.Task.IsRecurring ? 'Yes' : 'No' }}
                        </p>
                        <template v-if="taskError.Task.IsRecurring">
                            <p>
                                <strong>Frequency:</strong>
                                <br />
                                {{ taskError.Task.Frequency || '-' }}
                            </p>
                            <p>
                                <strong>Last Run:</strong>
                                <br />
                                {{ taskError.Task.LastRun ? formatDateTime(taskError.Task.LastRun) : '-' }}
                            </p>
                            <p>
                                <strong>Last Result:</strong>
                                <br />
                                {{ taskError.Task.LastResult || '-' }}
                            </p>
                            <p>
                                <strong>Recurrence:</strong>
                                <br />
                                {{ taskError.Task.Recurrence || '-' }}
                            </p>
                            <p>
                                <strong>Recurrence Count:</strong>
                                <br />
                                {{ taskError.Task.RecurrenceCount }}
                            </p>
                            <p>
                                <strong>Error Count</strong>
                                <br />
                                {{ taskError.Task.ErrorCount }}
                            </p>
                        </template>
                    </div>
                </tab>
                <tab title="Details">
                    <p>
                        <strong>Status Text:</strong>
                        <br />
                        {{ taskError.Task.StatusText || '-' }}
                    </p>
                    <p>
                        <strong>Data:</strong>
                        <br />
                        {{ taskError.Task.Data || '-' }}
                    </p>
                </tab>
                <tab title="Comments">
                    <TaskErrorComments
                        :taskError="taskError"
                        @save="(taskErrorId: string, comments: TaskErrorComment[]) => emit('saveComments', taskErrorId, comments)" />
                </tab>
            </Tabs>
        </template>
    </Modal>
</template>
